import { type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { pb } from '@/core/api';
import { Spinner } from '@/components/ui/spinner';

type CycleType = 'daily' | 'weekly' | 'monthly';

type Claim = {
  id: string;
  cycle_type: CycleType;
  cycle_start: string;
  cycle_end: string;
  winner: { id: string; first_name: string } | null;
  amount_eur: number;
  threshold: number;
  paid_in_payment: string;
};

type HistoryRes = { items: Claim[] };

const CYCLE_LABEL: Record<CycleType, string> = {
  daily: 'Diario',
  weekly: 'Semanal',
  monthly: 'Mensual',
};

function useRaceBonusHistory() {
  return useQuery({
    queryKey: ['race-bonus', 'history'],
    queryFn: (): Promise<HistoryRes> =>
      pb.send('/api/race-bonus/history?perPage=10', { method: 'GET' }),
    staleTime: 60_000,
  });
}

function fmtDate(d: string): string {
  return d.slice(5, 10);
}

function ClaimRow({ c }: { c: Claim }): ReactNode {
  const paid = !!c.paid_in_payment;
  return (
    <tr className="border-t border-white/[0.03] text-[11px] leading-tight">
      <td className="py-xs pr-xs text-text-tertiary">
        {CYCLE_LABEL[c.cycle_type] ?? '—'}
      </td>
      <td className="py-xs pr-xs text-text-tertiary">
        {fmtDate(c.cycle_start)}–{fmtDate(c.cycle_end)}
      </td>
      <td className="py-xs pr-xs text-text-secondary">
        {c.winner?.first_name ?? '—'}
      </td>
      <td className="py-xs pr-xs font-medium text-success">${c.amount_eur}</td>
      <td className="py-xs text-text-tertiary">{paid ? 'Pagado' : 'Pend.'}</td>
    </tr>
  );
}

function HistoryTable({ items }: { items: Claim[] }): ReactNode {
  return (
    <table className="w-full text-left">
      <thead>
        <tr className="text-[10px] uppercase text-text-tertiary/50">
          <th className="pb-xs pr-xs font-normal">Ciclo</th>
          <th className="pb-xs pr-xs font-normal">Fechas</th>
          <th className="pb-xs pr-xs font-normal">Ganador</th>
          <th className="pb-xs pr-xs font-normal">Premio</th>
          <th className="pb-xs font-normal">Estado</th>
        </tr>
      </thead>
      <tbody>
        {items.map((c) => (
          <ClaimRow key={c.id} c={c} />
        ))}
      </tbody>
    </table>
  );
}

export function RaceBonusHistory(): ReactNode {
  const { data, isLoading } = useRaceBonusHistory();
  if (isLoading) return <Spinner />;
  const items = data?.items ?? [];
  if (!items.length) {
    return (
      <p className="text-center text-[11px] text-text-tertiary">
        Sin carreras todavia
      </p>
    );
  }
  return <HistoryTable items={items} />;
}
